import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface OsintAiAnalysisPayload {
  headline: string;
  executiveSummary: string;
  confidence: 'low' | 'medium' | 'high';
  attributionStatus?: 'confirmed' | 'strong candidate' | 'supporting related profile' | 'weak mention';
  directQueryLinked?: boolean;
  officialSignalPresent?: boolean;
  contradictionFlags?: string[];
  primarySubject: {
    displayName: string;
    description: string;
    confidence: 'low' | 'medium' | 'high';
    attributionStatus?: 'confirmed' | 'strong candidate' | 'supporting related profile' | 'weak mention';
    directQueryLinked?: boolean;
    officialSignalPresent?: boolean;
  };
  socialProfiles: Array<{
    platform: string;
    sectionTitle: string;
    handle: string;
    displayName: string;
    profileUrl?: string;
    profileType: string;
    biography: string;
    metrics: string[];
    contentThemes: string[];
    contentThemeSummary: string;
    profileAssessment: string;
    analysisBullets: Array<{
      label: string;
      detail: string;
    }>;
    activitySummary: string;
    notableSignals: string[];
    crossPlatformLinks: string[];
    evidence: string[];
    sourceReferences: string[];
    evidenceTier?: 'tier1' | 'tier2' | 'tier3' | string;
    profileRole?: 'confirmed' | 'strong candidate' | 'supporting related profile' | 'weak mention' | string;
    directQueryLinked?: boolean;
    officialSignalPresent?: boolean;
    contradictionFlags?: string[];
    confidence: 'low' | 'medium' | 'high';
  }>;
  identityCandidates: Array<{
    name: string;
    description: string;
    confidence: 'low' | 'medium' | 'high';
    evidence: string[];
    directQueryLinked?: boolean;
    officialSignalPresent?: boolean;
    attributionStatus?: 'confirmed' | 'strong candidate' | 'supporting related profile' | 'weak mention' | string;
  }>;
  keyFindings: Array<{
    title: string;
    detail: string;
    evidence: string[];
  }>;
  relatedEntities: Array<{
    type:
      | 'email'
      | 'phone'
      | 'username'
      | 'wallet'
      | 'company'
      | 'address'
      | 'location'
      | 'platform'
      | 'other';
    value: string;
    context: string;
    evidence: string[];
  }>;
  detailBlocks: Array<{
    entityType: string;
    sectionTitle: string;
    value: string;
    summary: string;
    analysisBullets: Array<{
      label: string;
      detail: string;
    }>;
    sourceReferences: string[];
    evidenceTier?: 'tier1' | 'tier2' | 'tier3' | string;
    confidence: 'low' | 'medium' | 'high';
  }>;
  confirmedEvidence?: Array<{
    entityType: string;
    sectionTitle: string;
    value: string;
    summary: string;
    analysisBullets: Array<{
      label: string;
      detail: string;
    }>;
    sourceReferences: string[];
    evidenceTier?: 'tier1' | 'tier2' | 'tier3' | string;
    confidence: 'low' | 'medium' | 'high';
  }>;
  strongCandidateProfiles?: Array<any>;
  supportingRelatedProfiles?: Array<any>;
  officialProfessionalEvidence?: Array<any>;
  publicSocialEvidence?: Array<any>;
  crossPlatformCorroboration?: string[];
  nextLeads: string[];
  sourceReferences: string[];
  limitations: string[];
}

export interface OsintAiAnalysisResponse {
  analysis: OsintAiAnalysisPayload | null;
  generated_at: string | null;
  model: string | null;
  status: string | null;
  error: string | null;
  analysis_mode?: 'quick' | 'deep' | 'deep-cheap' | string | null;
  analysis_trace?: any;
  live_web_used?: boolean;
  analyst_feedback?: {
    status?: 'wrong' | 'correct' | 'needs-review' | string | null;
    note?: string | null;
    updated_at?: string | null;
    updated_by?: string | null;
  } | null;
}

@Injectable({
  providedIn: 'root',
})
export class OsintSearchService {
  baseUrl: string = environment.baseUrl;

  constructor(private http: HttpClient) { }

  // ONLY OSINT IMAGE SEARCH

  extractFacesFromImage(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/search/image`, payload);
  }

  getImageSearchJobId(payload: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/search/face`, payload);
  }

  getImageSearchResult(jobId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/search/${jobId}`);
  }

  updateImageSearchHistory(jobId: string, payload: any): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/search/update-history/${jobId}`,
      payload,
    );
  }

  convertImageToBase64(imageUrl: string): Observable<any> {
    const params = new HttpParams().set('url', imageUrl);
    return this.http.get(`${this.baseUrl}/search/convert/image-to-base64`, {
      params,
    });
  }

  /**
   * Local human-face presence check (no recognition).
   * Body: { url } or { urls: string[] }
   */
  detectHumanFaces(payload: {
    url?: string;
    urls?: string[];
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/osint/face-detect`, payload);
  }

  getImageSearchStaleResults(
    searchId: string,
    page: number = 1,
    size: number = 1000,
  ): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get(`${this.baseUrl}/search/results/${searchId}`, {
      params,
    });
  }

  // OSINT AND LEAK SEARCH

  /**
   * Step 1 — submit a new OSINT search.
   * POST `/osint/search` with phones | emails | usernames | names | wallets | gsts depending on `type`.
   * Returns job id(s) used by `getSearchResults` to poll vendor sync progress.
   */
  getJobIds(
    type: string,
    searchValues: string[],
    ipAddress: string,
    location: string,
  ): Observable<any> {
    const payload: any = {
      device: 'Chrome'
    };

    if (ipAddress != '') {
      payload.ip_address = ipAddress;
    }

    if (location != '') {
      payload.location = location;
    }

    if (type === 'phone') {
      payload.phones = searchValues;
    }

    if (type === 'email') {
      payload.emails = searchValues;
    }

    if (type === 'username') {
      payload.usernames = searchValues;
    }

    if (type === 'name') {
      payload.names = searchValues;
    }

    if (type === 'wallet') {
      payload.wallets = searchValues;
    }

    if (type === 'gst') {
      payload.gsts = searchValues;
    }

    if (type === 'din') {
      payload.dins = searchValues;
    }

    return this.http.post(`${this.baseUrl}/osint/search`, payload);
  }

  /**
   * Step 2 — poll job status while vendors sync.
   * POST `/osint/job` with `{ phone_job_ids | email_job_ids | … | gst_job_ids }` for the given `type`.
   */
  getSearchResults(type: string, jobIds: string[]): Observable<any> {
    const payload: any = {};

    if (type === 'phone') {
      payload.phone_job_ids = jobIds;
    }

    if (type === 'email') {
      payload.email_job_ids = jobIds;
    }

    if (type === 'username') {
      payload.username_job_ids = jobIds;
    }

    if (type === 'name') {
      payload.name_job_ids = jobIds;
    }

    if (type === 'wallet') {
      payload.wallet_job_ids = jobIds;
    }

    if (type === 'gst') {
      payload.gst_job_ids = jobIds;
    }

    if (type === 'din') {
      payload.din_job_ids = jobIds;
    }

    return this.http.post(`${this.baseUrl}/osint/job`, payload);
  }

  /**
   * Step 3 — fetch aggregated OSINT result rows once jobs complete.
   * GET `/osint/results/:searchId?type=<phone|email|username|name|wallet|gst>`.
   */
  getCompletedSearchResult(type: string, searchId: string): Observable<any> {
    const url: string = `${this.baseUrl}/osint/results/${searchId}`;
    const params = new HttpParams().set('type', type);

    return this.http.get(url, { params });
  }

  getAiAnalysis(
    type: string,
    searchId: string,
    mode: 'quick' | 'deep' | 'deep-cheap' = 'quick',
  ): Observable<{ data: OsintAiAnalysisResponse }> {
    const url: string = `${this.baseUrl}/osint/analysis/${searchId}`;
    const params = new HttpParams().set('type', type).set('mode', mode);

    return this.http.get<{ data: OsintAiAnalysisResponse }>(url, { params });
  }

  generateAiAnalysis(
    type: string,
    searchId: string,
    mode: 'quick' | 'deep' | 'deep-cheap' = 'quick',
    payload: {
      feedback_status?: 'wrong' | 'correct' | 'needs-review' | '';
      feedback_note?: string;
    } = {},
  ): Observable<{ data: OsintAiAnalysisResponse }> {
    const url: string = `${this.baseUrl}/osint/analysis/${searchId}`;
    const params = new HttpParams().set('type', type).set('mode', mode);

    return this.http.post<{ data: OsintAiAnalysisResponse }>(url, payload, { params });
  }

  /**
   * Export completed OSINT results.
   * GET `/osint/results/:searchId?type=…&is_export=true&format=<pdf|xlsx|…>`.
   */
  exportSearchResult(
    type: string,
    searchId: string,
    format: string,
  ): Observable<any> {
    const url: string = `${this.baseUrl}/osint/results/${searchId}`;
    const params = new HttpParams()
      .set('type', type)
      .set('is_export', true)
      .set('format', format);

    return this.http.get(url, { params });
  }
  updateEmailNotes(resultId: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/osint/email/${resultId}`, payload);
  }

  updatePhoneNotes(resultId: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/osint/phone/${resultId}`, payload);
  }

  updateUsernameNotes(resultId: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/osint/username/${resultId}`, payload);
  }

  updateNameNotes(resultId: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/osint/name/${resultId}`, payload);
  }

  updateWalletNotes(resultId: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/osint/wallet/${resultId}`, payload);
  }

  updateGstNotes(resultId: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/osint/gst/${resultId}`, payload);
  }

  updateDinNotes(resultId: string, payload: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/osint/din/${resultId}`, payload);
  }

  /** BTC deep-scan status / start (proxies vendor GET /api/deep-scan). */
  getWalletDeepScan(address: string, confirm = false): Observable<any> {
    let params = new HttpParams().set('address', address);
    if (confirm) {
      params = params.set('confirm', '1');
    }
    return this.http.get(`${this.baseUrl}/osint/wallet/deep-scan`, { params });
  }

  /** Multi-hop fund trace (proxies vendor POST /api/trace). */
  runWalletTrace(payload: {
    address: string;
    direction: 'downstream' | 'upstream';
    maxHops: number;
    osint_search_id?: string;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/osint/wallet/trace`, payload);
  }

  /** Saved fund-trace history for an investigation / address. */
  getWalletTraceHistory(params: {
    osint_search_id?: string;
    address?: string;
    limit?: number;
  }): Observable<any> {
    let httpParams = new HttpParams();
    if (params.osint_search_id) {
      httpParams = httpParams.set('osint_search_id', params.osint_search_id);
    }
    if (params.address) {
      httpParams = httpParams.set('address', params.address);
    }
    if (params.limit != null) {
      httpParams = httpParams.set('limit', String(params.limit));
    }
    return this.http.get(`${this.baseUrl}/osint/wallet/trace-history`, {
      params: httpParams,
    });
  }

  /** Transaction hash lookup (proxies vendor POST /api/transaction). */
  lookupWalletTransaction(hash: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/osint/wallet/transaction`, { hash });
  }

  /** Submit a community abuse report (proxies vendor POST /api/report). */
  submitWalletCommunityReport(payload: {
    address: string;
    category: string;
    note?: string;
  }): Observable<any> {
    return this.http.post(`${this.baseUrl}/osint/wallet/report`, payload);
  }
}
